import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { graphql, compose } from 'react-apollo';
import gql from 'graphql-tag';
import _ from 'lodash';
import { connect } from 'react-redux';
import { reset } from 'redux-form';

import messages from './messages';
import EditForm from './EditForm';
import { managedProjectsQuery } from './Table';
import { getErrorsFromApollo } from '../../../core/errors/util';
import errorMessages from '../../../core/errors/messages';
import Loading from '../../Loading';
import NotFound from '../../NotFound';
import { stringToDateOnly, dateOnlyToString } from '../../../core/dateonly';

class ManagedProjectEdit extends React.Component {
  static propTypes = {
    updateManagedProject: PropTypes.func.isRequired,
    managedProjectQuery: PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      managedProject: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          totalHoursOffered: PropTypes.number.isRequired,
          isActive: PropTypes.bool.isRequired,
          hourlyRate: PropTypes.number.isRequired,
          deadline: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          offeredPrice: PropTypes.number.isRequired,
          projectCategories: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string,
              name: PropTypes.string,
            }),
          ),
          projects: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string,
              name: PropTypes.string,
            }),
          ),
        }),
      ).isRequired,
    }).isRequired,
    projectsQuery: PropTypes.shape({
      loading: PropTypes.bool.isRequired,
      projects: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          visibility: PropTypes.string.isRequired,
          isLinked: PropTypes.bool.isRequired,
        }),
      ),
    }).isRequired,
    dispatch: PropTypes.func.isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    intl: intlShape.isRequired,
  };

  static defaultProps = {};

  constructor(props) {
    super(props);
    this.state = {
      errors: [],
    };
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  async handleSubmit(formValues) {
    const { managedProjectPositions, ...managedProject } = formValues;
    const filteredPositions = managedProjectPositions
      ? managedProjectPositions.filter(value => Object.keys(value).length !== 0)
      : [];

    if (managedProject.deadline)
      managedProject.deadline = stringToDateOnly(managedProject.deadline);

    const managedProjectPositionsPayload = filteredPositions.map(mpp => ({
      projectId: mpp.managedProjectPosition.value,
      name: mpp.managedProjectPosition.label,
      includeAllCategories: mpp.includeAllCategories,
      includeHoursBookedOnProject: mpp.includeHoursBookedOnProject,
      managedProjectPositionCategories:
        mpp.managedProjectPosition &&
        mpp.managedProjectPosition.managedProjectPositionCategories
          ? mpp.managedProjectPosition.managedProjectPositionCategories.map(
              mppc => ({
                id: mppc.value,
                name: mppc.label,
              }),
            )
          : null,
    }));

    await this.setState({ errors: [] });

    const payloadWithCategoriesAndProjects = {
      ...managedProject,
      managedProjectPositions: managedProjectPositionsPayload,
    };
    const payload = _.omit(payloadWithCategoriesAndProjects, ['__typename']);

    try {
      await this.props.updateManagedProject(payload);
    } catch (e) {
      const errors = getErrorsFromApollo(e);
      this.props.dispatch(reset('editForm'));
      this.setState({
        errors: this.state.errors.concat(
          errors.map(err =>
            this.props.intl.formatMessage(
              errorMessages[err.message],
              err.messageValues,
            ),
          ),
        ),
      });
    }
  }

  render() {
    const {
      projectsQuery,
      managedProjectQuery: { loading, managedProject },
    } = this.props;
    if (projectsQuery.loading && loading) return <Loading />;

    if (!managedProject) {
      return <NotFound />;
    }
    const {
      managedProjectPositions,
      ...managedProjectDestructred
    } = managedProject;

    let managedProjectPositionsPayload = [];
    if (managedProject.managedProjectPositions) {
      const filteredPositions = managedProjectPositions
        ? managedProjectPositions.filter(
            value => Object.keys(value).length !== 0,
          )
        : [];

      if (managedProjectDestructred.deadline)
        managedProjectDestructred.deadline = dateOnlyToString(
          managedProjectDestructred.deadline,
        );

      managedProjectPositionsPayload = filteredPositions.map(mpp => ({
        includeAllCategories: mpp.includeAllCategories,
        includeHoursBookedOnProject: mpp.includeHoursBookedOnProject,
        managedProjectPosition: {
          value: mpp.projectId,
          label: mpp.name,
          managedProjectPositionCategories: mpp.managedProjectPositionCategories
            ? mpp.managedProjectPositionCategories.map(mppc => ({
                value: mppc.id,
                label: mppc.name,
              }))
            : null,
        },
      }));
    }

    return (
      <div>
        <h2>
          <FormattedMessage {...messages.editManagedProject} />
        </h2>
        <EditForm
          managedProject={managedProject}
          projects={projectsQuery.projects}
          onSubmit={this.handleSubmit}
          errors={this.state.errors}
          initialValues={{
            ...managedProjectDestructred,
            managedProjectPositions: managedProjectPositionsPayload,
          }}
        />
      </div>
    );
  }
}

export const projectsQuery = gql`
  query projects {
    projects(showInactive: true) {
      id
      name
      visibility
      active
      isLinked
      categories {
        id
        name
      }
    }
  }
`;

const updateManagedProject = gql`
  mutation updateManagedProject($managedProject: EditManagedProjectInput!) {
    updateManagedProject(managedProject: $managedProject) {
      id
      name
      isActive
      deadline
      totalHoursOffered
      hourlyRate
      offeredPrice
      managedProjectPositions {
        id
        name
        includeAllCategories
        includeHoursBookedOnProject
        projectId
        managedProjectPositionCategories {
          id
          name
        }
      }
    }
  }
`;

export const managedProjectQuery = gql`
  query managedProject($id: String!) {
    managedProject(id: $id, showInactive: true) {
      id
      name
      isActive
      deadline
      totalHoursOffered
      hourlyRate
      offeredPrice
      managedProjectPositions {
        id
        name
        includeAllCategories
        includeHoursBookedOnProject
        projectId
        managedProjectPositionCategories {
          id
          name
        }
      }
    }
  }
`;

export default compose(
  injectIntl,
  graphql(managedProjectQuery, {
    name: 'managedProjectQuery',
    options: ({ match }) => ({
      variables: {
        id: match.params.id,
      },
    }),
  }),
  graphql(updateManagedProject, {
    props: ({ mutate }) => ({
      updateManagedProject: managedProject =>
        mutate({
          variables: { managedProject },
          update: (proxy, data) => {
            const newManagedProject = data.data
              ? data.data.createManagedProject
              : null;
            if (newManagedProject) {
              const managedProjectQueryData = proxy.readQuery({
                query: managedProjectsQuery,
              });
              managedProjectQueryData.managedProjects.push({
                ...newManagedProject,
              });
              managedProjectQueryData.managedProjects = managedProjectQueryData.managedProjects.sort(
                (a, b) => (a.name < b.name ? -1 : 1),
              );
              proxy.writeQuery({
                query: managedProjectsQuery,
                data: managedProjectQueryData,
              });
            }
          },
        }),
    }),
  }),
  graphql(projectsQuery, {
    name: 'projectsQuery',
  }),
  connect(),
)(ManagedProjectEdit);
